import React, { memo, useMemo } from 'react';
import styled from 'styled-components';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useStateSelector } from 'src/redux';
import { isEqual } from 'lodash';
import { CSkeleton, Tooltip } from 'src/common';

const Wrap = styled.div`
  position: relative;
  height: 78px;
  width: 90px;

  .highcharts-container {
    width: 100% !important;
    height: 100% !important;
  }

  .highcharts-root {
    overflow: visible !important;
  }
`;

const CenterText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Percentage = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  color: ${({ theme }) => theme.colors.prussianBlue};
  margin-bottom: 4px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
  color: ${({ theme }) => theme.colors.cflowerBlue};
`;

export interface ComplienceChartProps {
  controlsCount: number;
  controlsImplementedCount: number;
  controlsPlannedCount: number;
  animation?: boolean;
  isloading?: boolean;
}

const calculatePercentages = (
  implemented: number,
  planned: number,
  total: number,
): { compliantPercentage: number; partialPercentage: number; nonCompliantPercentage: number } => {
  if (total === 0) {
    return { compliantPercentage: 0, partialPercentage: 0, nonCompliantPercentage: 0 };
  }

  const nonCompliant = total - implemented - planned;

  // Calculate exact percentages (with decimals)
  const compliantExact = (implemented / total) * 100;
  const partialExact = (planned / total) * 100;
  const nonCompliantExact = (nonCompliant / total) * 100;

  // Floor each percentage and calculate remainders
  const compliantFloored = Math.floor(compliantExact);
  const partialFloored = Math.floor(partialExact);
  const nonCompliantFloored = Math.floor(nonCompliantExact);

  const compliantRemainder = compliantExact - compliantFloored;
  const partialRemainder = partialExact - partialFloored;
  const nonCompliantRemainder = nonCompliantExact - nonCompliantFloored;

  // Calculate how much we need to distribute
  const sum = compliantFloored + partialFloored + nonCompliantFloored;
  const toDistribute = 100 - sum;

  // Create array of [index, remainder] and sort by remainder descending
  // Priority order when remainders are equal: non-compliant > partial > compliant
  const remainders = [
    { index: 0, remainder: compliantRemainder, priority: 3 },
    { index: 1, remainder: partialRemainder, priority: 2 },
    { index: 2, remainder: nonCompliantRemainder, priority: 1 },
  ].sort((a, b) => {
    // First sort by remainder (descending)
    if (Math.abs(a.remainder - b.remainder) > 0.0001) {
      return b.remainder - a.remainder;
    }
    // If remainders are equal, sort by priority (ascending - lower priority value = higher priority)
    return a.priority - b.priority;
  });

  // Distribute the remaining percentage points to those with largest remainders
  const percentages = [compliantFloored, partialFloored, nonCompliantFloored];
  for (let i = 0; i < toDistribute; i++) {
    percentages[remainders[i].index]++;
  }

  return {
    compliantPercentage: percentages[0],
    partialPercentage: percentages[1],
    nonCompliantPercentage: percentages[2],
  };
};

export const ComplienceChart: React.FC<ComplienceChartProps> = memo(
  ({ controlsCount, controlsImplementedCount, controlsPlannedCount, animation = false, isloading = false }) => {
    const colors = useStateSelector(({ theme }) => theme.colors);

    const { compliantPercentage, partialPercentage, nonCompliantPercentage } = useMemo(() => {
      return calculatePercentages(controlsImplementedCount, controlsPlannedCount, controlsCount);
    }, [controlsImplementedCount, controlsCount, controlsPlannedCount]);

    const afterPlannedPercentage = useMemo(() => {
      if (controlsCount === 0) return 0;
      return Math.round(((controlsImplementedCount + controlsPlannedCount) / controlsCount) * 100);
    }, [controlsImplementedCount, controlsPlannedCount, controlsCount]);

    const chartData = useMemo(
      () => [
        {
          name: 'Compliant',
          y: compliantPercentage,
          color: colors.statusGreen,
        },
        {
          name: 'Partial',
          y: partialPercentage,
          color: colors.brightBlue,
        },
        {
          name: 'Non-Compliant',
          y: nonCompliantPercentage,
          color: '#D9D9D9',
        },
      ],
      [compliantPercentage, partialPercentage, nonCompliantPercentage, colors],
    );

    const chartOptions = useMemo(
      () => ({
        chart: {
          type: 'pie',
          backgroundColor: 'transparent',
          height: 90,
          width: 90,
          margin: [0, 0, 0, 0],
          spacing: [0, 0, 0, 0],
          spacingTop: 0,
          spacingBottom: 0,
          spacingLeft: 0,
          spacingRight: 0,
        },
        title: {
          text: '',
        },
        plotOptions: {
          pie: {
            startAngle: -135,
            endAngle: 135,
            innerSize: '85%',
            dataLabels: {
              enabled: false,
            },
            showInLegend: false,
            borderWidth: 0,
            // borderRadius: 0,
            slicedOffset: 0,
            enableMouseTracking: false,
            states: {
              hover: {
                enabled: false,
                halo: false,
              },
              inactive: {
                enabled: false,
              },
            },
          },
        },
        series: [
          {
            name: 'Compliance',
            data: chartData,
            animation,
          },
        ],
        tooltip: {
          enabled: false,
        },
        credits: {
          enabled: false,
        },
        legend: {
          enabled: false,
        },
      }),
      [chartData, colors, animation],
    );

    if (isloading) {
      return <CSkeleton height={78} width={90} borderRadius={20} />;
    }

    return (
      <>
        <Wrap data-cy="compliance-chart">
          <Tooltip
            positionStrategy="fixed"
            dataId="compliance-chart"
            place="right"
            dataHtml={`<div style="display: flex; flex-direction: column; font-size: 14px;font-weight:500">
              <span style="margin-bottom:4px; font-weight: 700">Compliance:</span>
              <div style="display: flex; align-items: center; gap: 4px;"><div style="width: 10px; height: 10px; background-color: ${colors.statusGreen}; border-radius: 50%;"></div><span data-cy="compliant-percentage">${compliantPercentage}%- Current</span></div>
              <div style="display: flex; align-items: center; gap: 4px;"><div style="width: 10px; height: 10px; background-color: ${colors.brightBlue}; border-radius: 50%;"></div>${partialPercentage ? `<span data-cy="partial-percentage">${afterPlannedPercentage}%- After planned controls are implemented (+${afterPlannedPercentage - compliantPercentage}%)</span>` : `<span data-cy="no-planned-controls">No planned controls</span>`}</div>
              </div>`}
          >
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
              allowChartUpdate={true}
              updateArgs={[true, true, true]}
            />
            <CenterText>
              <Percentage>{compliantPercentage}%</Percentage>
              <Label>compliant</Label>
            </CenterText>
          </Tooltip>
        </Wrap>
      </>
    );
  },
  isEqual,
);
